import { useEffect, useState, useRef } from "react";
import { queue } from "../lib/api";
import type { QueueSession, QueueStats } from "../lib/api";
import {
  RefreshCw,
  Trash2,
  Clock,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Timer,
  Play,
  ExternalLink,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import clsx from "clsx";

const STATUS_CONFIG: Record<
  QueueSession["status"],
  { color: string; icon: typeof Clock; label: string }
> = {
  waiting: { color: "text-warning", icon: Clock, label: "Waiting" },
  ready: { color: "text-success", icon: CheckCircle, label: "Ready" },
  completed: { color: "text-info", icon: CheckCircle, label: "Completed" },
  expired: { color: "text-error", icon: XCircle, label: "Expired" },
  error: { color: "text-error", icon: AlertTriangle, label: "Error" },
};

export default function QueuePage() {
  const [sessions, setSessions] = useState<{
    active: QueueSession[];
    history: QueueSession[];
  }>({ active: [], history: [] });
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);

  const fetchData = async () => {
    try {
      const [sessionsData, statsData] = await Promise.all([
        queue.list(),
        queue.getStats(),
      ]);
      setSessions(sessionsData);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch queue data", error);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket for real-time updates
  useEffect(() => {
    fetchData();

    const apiHost =
      import.meta.env.VITE_API_URL?.replace(/^https?:\/\//, "") || "localhost:3001";
    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${wsProtocol}//${apiHost}/api/queue/stream`;

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log("Queue stream connected");
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "init") {
            setSessions((prev) => ({ ...prev, active: data.sessions }));
            setStats(data.stats);
            setLoading(false);
          } else if (data.type.startsWith("queue:")) {
            // Refresh on any queue event
            fetchData();
          }
        } catch (e) {
          console.error("Failed to parse queue message", e);
        }
      };

      ws.onerror = () => {
        console.warn("Queue WebSocket failed, using polling");
      };

      wsRef.current = ws;
    } catch (err) {
      console.warn("Failed to create WebSocket", err);
    }

    // Fallback polling
    const interval = setInterval(fetchData, 5000);

    return () => {
      if (wsRef.current) wsRef.current.close();
      clearInterval(interval);
    };
  }, []);

  const handleMarkReady = async (id: string) => {
    try {
      await queue.markReady(id);
      fetchData();
    } catch (error) {
      console.error("Failed to mark ready", error);
    }
  };

  const handleMarkComplete = async (id: string) => {
    try {
      await queue.markComplete(id);
      fetchData();
    } catch (error) {
      console.error("Failed to mark complete", error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Remove this queue session?")) return;
    try {
      await queue.delete(id);
      fetchData();
    } catch (error) {
      console.error("Failed to delete session", error);
    }
  };

  const handleClearOld = async () => {
    try {
      const result = await queue.clearOld();
      alert(`Cleared ${result.clearedMemory + result.clearedDb} old sessions`);
      fetchData();
    } catch (error) {
      console.error("Failed to clear old sessions", error);
    }
  };

  const formatWaitTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="animate-spin text-primary-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold">Queue Manager</h2>
          <p className="text-sm text-gray-400 mt-1">
            Track and manage Pokemon Center queue sessions
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
            <RefreshCw size={16} />
            Refresh
          </button>
          <button onClick={handleClearOld} className="btn-secondary flex items-center gap-2">
            <Trash2 size={16} />
            Clear Old
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-6 gap-3">
          <div className="card py-3">
            <p className="text-xs text-gray-400">Waiting</p>
            <p className="text-2xl font-bold text-warning">{stats.waiting}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Ready</p>
            <p className="text-2xl font-bold text-success">{stats.ready}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Completed</p>
            <p className="text-2xl font-bold text-info">{stats.completed}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Expired</p>
            <p className="text-2xl font-bold text-error">{stats.expired}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Errors</p>
            <p className="text-2xl font-bold text-error">{stats.errors}</p>
          </div>
          <div className="card py-3">
            <p className="text-xs text-gray-400">Avg Wait</p>
            <p className="text-2xl font-bold">
              {stats.averageWaitTime ? formatWaitTime(stats.averageWaitTime) : "-"}
            </p>
          </div>
        </div>
      )}

      {/* Active Sessions */}
      <div>
        <h3 className="font-semibold mb-3 flex items-center gap-2">
          <Clock size={18} className="text-warning" />
          Active Sessions ({sessions.active.length})
        </h3>

        {sessions.active.length === 0 ? (
          <div className="card text-center py-8">
            <Timer size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">No active queue sessions</p>
            <p className="text-sm text-gray-500 mt-1">
              Queue sessions are created automatically when Pokemon Center puts you in a queue
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.active.map((session) => {
              const config = STATUS_CONFIG[session.status];
              const Icon = config.icon;
              const elapsed = session.startedAt
                ? formatDistanceToNow(new Date(session.startedAt), { addSuffix: false })
                : null;

              return (
                <div key={session.id} className="card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div
                        className={clsx(
                          "p-3 rounded-lg",
                          session.status === "waiting" && "bg-warning/20",
                          session.status === "ready" && "bg-success/20"
                        )}
                      >
                        <Icon size={24} className={config.color} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={clsx("font-semibold", config.color)}>
                            {config.label}
                          </span>
                          <span className="text-xs text-gray-500">{session.retailer}</span>
                        </div>
                        {session.task && (
                          <p className="text-sm text-gray-400">
                            Task: {session.task.product.name}
                          </p>
                        )}
                        <p className="text-xs text-gray-500 truncate max-w-md">
                          {session.targetUrl}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-6">
                      {/* Queue Position */}
                      {session.position !== null && session.position !== undefined && (
                        <div className="text-center">
                          <p className="text-2xl font-bold text-primary-500">
                            #{session.position}
                          </p>
                          <p className="text-xs text-gray-500">Position</p>
                        </div>
                      )}

                      {/* Users Ahead */}
                      {session.usersAhead !== null && session.usersAhead !== undefined && (
                        <div className="text-center">
                          <p className="text-lg font-semibold flex items-center gap-1">
                            <Users size={16} />
                            {session.usersAhead.toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">Ahead</p>
                        </div>
                      )}

                      {/* Estimated Wait */}
                      {session.estimatedWait && (
                        <div className="text-center">
                          <p className="text-lg font-semibold">
                            {formatWaitTime(session.estimatedWait)}
                          </p>
                          <p className="text-xs text-gray-500">Est. Wait</p>
                        </div>
                      )}

                      {/* Elapsed */}
                      {elapsed && (
                        <div className="text-center">
                          <p className="text-lg font-semibold text-gray-400">{elapsed}</p>
                          <p className="text-xs text-gray-500">Elapsed</p>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2">
                        {session.status === "waiting" && (
                          <button
                            onClick={() => handleMarkReady(session.id)}
                            className="btn-primary p-2"
                            title="Mark as Ready"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                        {session.status === "ready" && (
                          <>
                            <a
                              href={session.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn-primary p-2"
                              title="Open Page"
                            >
                              <ExternalLink size={16} />
                            </a>
                            <button
                              onClick={() => handleMarkComplete(session.id)}
                              className="btn-secondary p-2"
                              title="Mark Complete"
                            >
                              <Play size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(session.id)}
                          className="p-2 hover:bg-dark-600 rounded-lg"
                          title="Remove"
                        >
                          <Trash2 size={16} className="text-error" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar for waiting */}
                  {session.status === "waiting" &&
                    session.estimatedWait &&
                    session.startedAt && (
                      <div className="mt-4 pt-4 border-t border-dark-600">
                        <div className="h-2 bg-dark-600 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-warning to-success transition-all duration-1000"
                            style={{
                              width: `${Math.min(
                                100,
                                (Date.now() - new Date(session.startedAt).getTime()) /
                                  (session.estimatedWait * 60 * 1000) *
                                  100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* History */}
      <div>
        <h3 className="font-semibold mb-3">Recent History</h3>
        {sessions.history.length === 0 ? (
          <div className="card text-center py-6">
            <p className="text-gray-400">No queue history yet</p>
          </div>
        ) : (
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead className="bg-dark-700">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">
                    Status
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">
                    Target
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">
                    Started
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">
                    Duration
                  </th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">
                    Position
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {sessions.history.slice(0, 20).map((session) => {
                  const config = STATUS_CONFIG[session.status];
                  const Icon = config.icon;
                  const duration =
                    session.completedAt && session.startedAt
                      ? Math.round(
                          (new Date(session.completedAt).getTime() -
                            new Date(session.startedAt).getTime()) /
                            1000 /
                            60
                        )
                      : null;

                  return (
                    <tr key={session.id} className="hover:bg-dark-700/50">
                      <td className="px-4 py-3">
                        <span className={clsx("flex items-center gap-2", config.color)}>
                          <Icon size={14} />
                          {config.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400 truncate max-w-xs">
                        {session.task?.product.name || session.targetUrl}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {format(new Date(session.startedAt), "MMM d, h:mm a")}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {duration ? formatWaitTime(duration) : "-"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {session.position || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

